import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect, useRef} from 'react';
import {MenuBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/menu-bottom-bar';
import {PosterEditorAddItemButton} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/poster-editor-add-item-button';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {DefaultBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/default-bottom-bar';
import {VectorBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/vector-bottom-bar';
import {QrBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/qr-bottom-bar';
import {ImageBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/image-bottom-bar';
import {TextBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/text-bottom-bar';
import {FancyTextBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/fancy-text-bottom-bar';
import {VideoBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/video-bottom-bar';
import {TableBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/table-bottom-bar';
import {TabsBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/tabs-bottom-bar';
import {DrawModeBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/draw-mode-bottom-bar';
import {Button, Size, Type} from '@Components/button';
import {SlideshowBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/slideshow-bottom-bar';
import {StickerBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/sticker-bottom-bar';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {RectangleBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/rectangle-bottom-bar/rectangle-bottom-bar';
import {LineBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/line-bottom-bar/line-bottom-bar';
import {TranscriptBottomBar} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/transcript-bottom-bar/transcript-bottom-bar';
import {useAppSelector} from '@/hooks';
import styles from './poster-editing-bottom-bar.module.scss';
import {useGetNumberOfActiveItems} from '@Hooks/poster-editor/useGetNumberOfActiveItems';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';

interface PosterEditorBottomBarProps {
  isBottomSheetOverlapping?: boolean;
}

export function PosterEditorBottomBar({isBottomSheetOverlapping = false}: PosterEditorBottomBarProps): ReactElement<any> {
  const numberOfActiveItems = useGetNumberOfActiveItems();
  const selectedItemId = useSingleActivePosterItemId();
  const isDrawModeActive = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isDrawModeOn;
  });
  const isImageGeneratingFromDrawing = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isImageBeingGenerated;
  });
  const showSkeleton = useShowContextualSkeletons();
  const posterEditorBottomBarEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (posterEditorBottomBarEl.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_MOBILE_BOTTOM_EDITING_BAR] = $(posterEditorBottomBarEl.current);
    }
    return () => {
      if (window.posterEditor) {
        window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_MOBILE_BOTTOM_EDITING_BAR] = undefined;
      }
    };
  }, []);
  const getBottomBar = (): ReactElement<any> | null => {
    if (showSkeleton) {
      return <DefaultBottomBar showSkeleton />;
    }

    if (numberOfActiveItems > 1) {
      return null;
    }

    if (isDrawModeActive || isImageGeneratingFromDrawing) {
      return <DrawModeBottomBar />;
    }

    if (numberOfActiveItems === 0) {
      return <DefaultBottomBar />;
    }

    if (numberOfActiveItems === 1 && selectedItemId) {
      const activeItem = window.posterEditor?.whiteboard?.getItemForId(selectedItemId);
      switch (activeItem?.gitype) {
        case ITEM_TYPE.VECTOR:
          return <VectorBottomBar />;
        case ITEM_TYPE.RECTANGLE:
          return <RectangleBottomBar />;
        case ITEM_TYPE.QR_CODE:
          return <QrBottomBar />;
        case ITEM_TYPE.IMAGE:
          return <ImageBottomBar />;
        case ITEM_TYPE.STICKER:
          return <StickerBottomBar />;
        case ITEM_TYPE.TEXT:
          return <TextBottomBar />;
        case ITEM_TYPE.FANCY_TEXT:
          return <FancyTextBottomBar />;
        case ITEM_TYPE.VIDEO:
          return <VideoBottomBar />;
        case ITEM_TYPE.TABLE:
          return <TableBottomBar />;
        case ITEM_TYPE.MENU:
          return <MenuBottomBar />;
        case ITEM_TYPE.SLIDESHOW:
          return <SlideshowBottomBar />;
        case ITEM_TYPE.TAB:
          return <TabsBottomBar />;
        case ITEM_TYPE.LINE:
          return <LineBottomBar />;
        case ITEM_TYPE.TRANSCRIPT:
          return <TranscriptBottomBar />;
        default:
          return <DefaultBottomBar />;
      }
    }
    return <DefaultBottomBar />;
  };

  const onFinishDrawing = (): void => {
    window.posterEditor?.whiteboard?.drawing.toggleDrawMode();
  };

  return useMemo((): ReactElement<any> => {
    return (
      <div
        ref={posterEditorBottomBarEl}
        className={`flex-v-row _full-width spacing-p-t-1 ${styles.bottomOptions}  ${isBottomSheetOverlapping ? styles.overlappingBottomSheet : ''}`}
      >
        {isDrawModeActive || isImageGeneratingFromDrawing ? (
          <Button
            isLoading={isImageGeneratingFromDrawing}
            customClasses={styles.drawButton}
            type={Type.PRIMARY}
            size={Size.SMALL}
            onClick={onFinishDrawing}
            text={window.i18next.t('pmwjs_finish_drawing')}
          />
        ) : null}
        <div className={`flex-h-row ${styles.bottomBarContainer}`}>
          <PosterEditorAddItemButton showSkeletonState={showSkeleton} />
          {getBottomBar()}
        </div>
      </div>
    );
  }, [numberOfActiveItems, selectedItemId, isDrawModeActive, isImageGeneratingFromDrawing, showSkeleton, isBottomSheetOverlapping]);
}
